import { SiweMessage } from "siwe";
import { ethers } from "ethers";
import { fetchNonce, verifySignature } from "utils/apiService/Auth";

/**
 * Signs in with Ethereum using SIWE protocol
 * @returns {Promise<{token: string, user: any}>} User data and authentication token
 */
export const signInWithEthereum = async (): Promise<{
  token: string;
  user: any;
}> => {
  // Check if ethereum is available
  if (!(window as any).ethereum) {
    throw new Error(
      "No Ethereum provider found. Please install MetaMask or another wallet."
    );
  }

  try {
    // Step 1: Fetch nonce from API
    const nonce = await fetchNonce();

    // Step 2: Connect to wallet and get signer
    const provider = new ethers.providers.Web3Provider(
      (window as any).ethereum
    );
    await provider.send("eth_requestAccounts", []); // Request account access
    const signer = provider.getSigner();
    const address = await signer.getAddress();

    // Step 3: Get network info
    const network = await provider.getNetwork();
    const chainId = network.chainId;

    // Step 4: Create SIWE message
    // const domain = window.location.host;
    const domain = "www.pinksale.finance";
    const origin = window.location.origin;
    const statement = "Sign in with Ethereum to the app.";

    // Calculate expiration time (24 hours from now)
    const issuedAt = new Date().toISOString();
    const expirationTime = new Date(
      Date.now() + 24 * 60 * 60 * 1000
    ).toISOString();

    const siweMessage = new SiweMessage({
      domain,
      address,
      statement,
      uri: origin,
      version: "1",
      chainId: chainId,
      nonce,
      issuedAt,
      expirationTime,
    });

    // Step 5: Prepare and sign message
    const messageToSign = siweMessage.prepareMessage();
    const signature = await signer.signMessage(messageToSign);

    // Step 6: Verify signature with API
    const result = await verifySignature({
      message: messageToSign,
      signature,
      type: "EVM",
      domain,
      chainId: chainId,
    });

    // Extract token and user data
    const { token, ...user } = result;

    return {
      token,
      user,
    };
  } catch (error: any) {
    // Re-throw with more context if needed
    if (error.message) {
      throw error;
    }
    throw new Error(`SIWE authentication failed: ${error.toString()}`);
  }
};
